import { useState, useEffect } from 'react';
import { teamAPI, rbacAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Users, Shield, Star, User, BarChart3, Trophy, UserPlus, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const DEFAULT_ROLE_CONFIG = {
  admin: { icon: Shield, color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', label: 'Admin' },
  manager: { icon: Star, color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', label: 'Manager' },
  agent: { icon: User, color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', label: 'Trainer' },
};

export default function TeamPage() {
  const [members, setMembers] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [neuDialog, setNeuDialog] = useState(false);
  const [neu, setNeu] = useState({ name: '', email: '', password: '', role: 'agent' });
  const [legtAn, setLegtAn] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const fetch = async () => {
      try {
        const [teamRes, rolesRes] = await Promise.all([
          teamAPI.list(),
          user?.role === 'admin' ? rbacAPI.getRoles().catch(() => ({ data: { default_roles: [], custom_roles: [] } })) : Promise.resolve({ data: { default_roles: [], custom_roles: [] } }),
        ]);
        setMembers(teamRes.data);
        const defaults = (rolesRes.data.default_roles || []).map(r => ({ id: r.id, name: r.name }));
        const custom = (rolesRes.data.custom_roles || []).map(r => ({ id: r.id, name: r.name }));
        setAllRoles([...defaults, ...custom]);
      } catch (e) { console.error('Team laden fehlgeschlagen:', e); } finally { setLoading(false); }
    };
    fetch();
  }, [user]);

  /* Passwort setzen (28.09.2026): Admin tippt ein neues Passwort fuer einen
     Mitarbeiterzugang, z. B. wenn es vergessen wurde. Der Betroffene meldet sich
     damit an und aendert es im Profil. */
  const [pwFuer, setPwFuer] = useState(null);
  const [pwNeu, setPwNeu] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const passwortSetzen = async () => {
    if (!pwFuer) return;
    if (pwNeu.length < 8) { toast.error('Mindestens 8 Zeichen'); return; }
    setPwBusy(true);
    try { const r = await teamAPI.setPassword(pwFuer.user_id, pwNeu); toast.success(r.data.message); setPwFuer(null); setPwNeu(''); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Passwort nicht gesetzt'); }
    finally { setPwBusy(false); }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await teamAPI.updateRole(userId, newRole);
      toast.success('Rolle aktualisiert');
      const roleName = DEFAULT_ROLE_CONFIG[newRole]?.label || allRoles.find(r => r.id === newRole)?.name || newRole;
      setMembers(prev => prev.map(m => m.user_id === userId ? { ...m, role: newRole, role_name: roleName } : m));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehlgeschlagen');
    }
  };

  const anlegen = async () => {
    if (!neu.name.trim() || !neu.email.trim() || !neu.password) {
      toast.error('Bitte Name, E-Mail und Passwort ausfüllen'); return;
    }
    setLegtAn(true);
    try {
      const res = await teamAPI.createUser(neu);
      toast.success(res.data.message);
      setNeuDialog(false);
      setNeu({ name: '', email: '', password: '', role: 'agent' });
      const teamRes = await teamAPI.list();
      setMembers(teamRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Zugang konnte nicht angelegt werden');
    } finally {
      setLegtAn(false);
    }
  };

  const getRoleConfig = (roleId, roleName) => {
    if (DEFAULT_ROLE_CONFIG[roleId]) return DEFAULT_ROLE_CONFIG[roleId];
    return {
      icon: Shield,
      color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
      label: roleName || roleId,
    };
  };


  if (loading) return <div className="h-64 flex items-center justify-center"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  if (user?.role === 'agent') {
    return (
      <div className="space-y-5 fade-in" data-testid="team-page">
        <h1 className="text-2xl font-bold tracking-tight">Team</h1>
        <Card className="border-border/60"><CardContent className="p-8 text-center text-muted-foreground">
          <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Teamverwaltung ist für Manager und Admins verfügbar.</p>
        </CardContent></Card>
      </div>
    );
  }

  // Group role counts
  const roleCounts = {};
  members.forEach(m => {
    const label = getRoleConfig(m.role, m.role_name).label;
    roleCounts[label] = (roleCounts[label] || 0) + 1;
  });

  return (
    <div className="space-y-5 fade-in" data-testid="team-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Team</h1>
          <p className="text-muted-foreground text-xs sm:text-sm">{members.length} Mitarbeiter</p>
        </div>
        <button onClick={() => setNeuDialog(true)} data-testid="btn-mitarbeiter-anlegen"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-bold hover:opacity-90">
          <UserPlus className="w-4 h-4" /> Mitarbeiter anlegen
        </button>
      </div>

      {neuDialog && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setNeuDialog(false)}>
          <div className="bg-background rounded-2xl w-full max-w-md p-5 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="mitarbeiter-dialog">
            <div className="flex items-center justify-between">
              <h3 className="font-bold">Mitarbeiter-Zugang anlegen</h3>
              <button onClick={() => setNeuDialog(false)} className="p-1 rounded hover:bg-muted"><X className="w-4 h-4" /></button>
            </div>
            <input value={neu.name} onChange={(e) => setNeu(n => ({ ...n, name: e.target.value }))} placeholder="Name"
              className="w-full rounded-xl border border-border bg-background p-2.5 text-sm" data-testid="neu-name" />
            <input value={neu.email} onChange={(e) => setNeu(n => ({ ...n, email: e.target.value }))} placeholder="E-Mail" type="email"
              className="w-full rounded-xl border border-border bg-background p-2.5 text-sm" data-testid="neu-email" />
            <input value={neu.password} onChange={(e) => setNeu(n => ({ ...n, password: e.target.value }))} placeholder="Passwort (min. 8 Zeichen)" type="text"
              className="w-full rounded-xl border border-border bg-background p-2.5 text-sm" data-testid="neu-passwort" />
            <select value={neu.role} onChange={(e) => setNeu(n => ({ ...n, role: e.target.value }))}
              className="w-full rounded-xl border border-border bg-background p-2.5 text-sm" data-testid="neu-rolle">
              <option value="agent">Trainer</option>
              <option value="manager">Manager</option>
              {user?.role === 'admin' && <option value="admin">Admin</option>}
              {allRoles.filter(r => !['admin', 'manager', 'agent'].includes(r.id)).map(r => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
            <button onClick={anlegen} disabled={legtAn}
              className="w-full py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-bold hover:opacity-90 disabled:opacity-50" data-testid="neu-anlegen">
              {legtAn ? 'Wird angelegt…' : 'Zugang anlegen'}
            </button>
          </div>
        </div>
      )}

      {pwFuer && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setPwFuer(null)}>
          <div className="bg-background rounded-2xl w-full max-w-md p-5 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="passwort-dialog">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold">Passwort setzen · {pwFuer.name}</h2>
              <button onClick={() => setPwFuer(null)} className="p-1 rounded hover:bg-muted"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-xs text-muted-foreground">{pwFuer.email} meldet sich danach mit diesem Passwort an. Am besten gleich persönlich weitergeben, nicht per Nachricht.</p>
            <input value={pwNeu} onChange={(e) => setPwNeu(e.target.value)} placeholder="Neues Passwort (min. 8 Zeichen)" type="text" autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') passwortSetzen(); }}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="pw-neu" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setPwFuer(null)} className="px-3 h-9 rounded-md border border-border text-sm">Abbrechen</button>
              <button onClick={passwortSetzen} disabled={pwBusy || pwNeu.length < 8} data-testid="pw-speichern"
                className="px-3 h-9 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-50">{pwBusy ? '…' : 'Passwort setzen'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="flex flex-wrap gap-3">
        {Object.entries(roleCounts).map(([label, count]) => (
          <Card key={label} className="border-border/60">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                <Shield className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="text-xl font-bold">{count}</p>
                <p className="text-xs text-muted-foreground">{label}{count !== 1 ? 's' : ''}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Members List */}
      <Card className="border-border/60">
        <CardHeader className="pb-3"><CardTitle className="text-sm">Alle Mitarbeiter</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-3">
            {members.map((member) => {
              const config = getRoleConfig(member.role, member.role_name);
              const Icon = config.icon;
              return (
                <div key={member.user_id} className="flex items-center justify-between py-3 border-b border-border/30 last:border-0" data-testid={`team-member-${member.user_id}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                      {member.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium">{member.name}</p>
                        <Badge variant="secondary" className={`text-[10px] ${config.color}`}>
                          <Icon className="w-3 h-3 mr-1" />{config.label}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">{member.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right hidden md:block">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1.5">
                          <BarChart3 className="w-3.5 h-3.5 text-muted-foreground" />
                          <span className="text-xs">{member.lead_count || 0} Mitglieder</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Trophy className="w-3.5 h-3.5 text-amber-500" />
                          <span className="text-xs">{member.won_count || 0} gewonnen</span>
                        </div>
                      </div>
                    </div>
                    {user?.role === 'admin' && member.user_id !== user.user_id && (
                      <button type="button" onClick={() => { setPwFuer(member); setPwNeu(''); }} data-testid={`pw-${member.user_id}`}
                        className="text-xs text-muted-foreground hover:text-foreground underline whitespace-nowrap">Passwort setzen</button>
                    )}
                    {user?.role === 'admin' && member.user_id !== user.user_id && (
                      <select
                        value={member.role}
                        onChange={(e) => handleRoleChange(member.user_id, e.target.value)}
                        data-testid={`role-select-${member.user_id}`}
                        className="flex h-8 w-[150px] rounded-md border border-input bg-background px-2 text-xs shadow-sm"
                      >
                        {allRoles.map(r => (
                          <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
